import { useMemo, useState, type ReactNode } from 'react'
import { ZIP3, zipCentroid } from '../../shared/zip3'
import s from './Chart.module.css'

export interface ZipPoint {
  /** A 5-digit zip, ZIP+4, or 3-digit prefix. Points are grouped by 3-digit prefix. */
  zip: string
  value: number
}

export interface ZipLayer {
  /** Legend label, e.g. "Customers" or "Retailers". */
  name: string
  points: ZipPoint[]
  /** Defaults: first layer the accent colour as circles, second layer ink as squares. */
  color?: string
  shape?: 'circle' | 'square'
}

/**
 * A map of the continental US with one dot per 3-digit zip prefix, sized by value. Pass one layer, or two to
 * compare (customers against retailers, say). The faint dots are every prefix, so the country's outline shows
 * without a basemap. Alaska, Hawaii and territories are counted in a note below the map. Hover, tap or tab to a dot
 * for its number. `empty` shows instead when no layer has a point.
 */
export function ZipMap(props: { layers: ZipLayer[]; format?: (v: number) => string; height?: number; empty: ReactNode }) {
  const [hover, setHover] = useState<{ x: number; y: number; text: string } | null>(null)
  const fmt = props.format ?? ((v: number) => String(v))
  const W = 640
  const H = props.height ?? 400

  const { background, layers, outside } = useMemo(() => {
    const fit = fitter(W, H)
    const background = [...ZIP3.values()].filter(inContiguous).map(([lat, lon]) => fit(lat, lon))
    let outside = 0
    const layers = props.layers.map((layer, li) => {
      const byPrefix = new Map<string, number>()
      for (const p of layer.points) {
        const prefix = p.zip.replace(/\D/g, '').slice(0, 3)
        if (prefix.length === 3) byPrefix.set(prefix, (byPrefix.get(prefix) ?? 0) + p.value)
      }
      const max = Math.max(1, ...byPrefix.values())
      const marks: { prefix: string; x: number; y: number; r: number; value: number }[] = []
      for (const [prefix, value] of byPrefix) {
        const c = zipCentroid(prefix)
        if (!c || !inContiguous(c)) {
          outside += value
          continue
        }
        const [x, y] = fit(c[0], c[1])
        marks.push({ prefix, x, y, value, r: 3 + Math.sqrt(value / max) * 13 })
      }
      marks.sort((a, b) => b.r - a.r) // big ones underneath
      return {
        name: layer.name,
        color: layer.color ?? (li === 0 ? 'var(--chart-1)' : 'var(--text-1)'),
        shape: layer.shape ?? (li === 0 ? 'circle' : 'square'),
        marks,
      }
    })
    return { background, layers, outside }
  }, [props.layers, H])

  if (props.layers.every((l) => l.points.length === 0)) return <>{props.empty}</>
  return (
    <div className={`${s.chart} ${s.map}`}>
      {layers.length > 1 && (
        <div className={s.legend}>
          {layers.map((l) => (
            <span key={l.name}>
              <i style={{ background: l.color, borderRadius: l.shape === 'circle' ? '50%' : 2 }} />
              {l.name}
            </span>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Map: ${layers.map((l) => l.name).join(' and ')}`} onMouseLeave={() => setHover(null)}>
        {background.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={1.6} fill="var(--chart-dot)" />
        ))}
        {layers.map((l) =>
          l.marks.map((m) => {
            const text = `${m.prefix}xx · ${layers.length > 1 ? `${l.name}: ` : ''}${fmt(m.value)}`
            const enter = () => setHover({ x: m.x, y: m.y - m.r, text })
            return l.shape === 'circle' ? (
              <circle key={`${l.name}-${m.prefix}`} cx={m.x} cy={m.y} r={m.r} fill={l.color} fillOpacity={0.72}
                stroke="var(--surface-1)" strokeWidth={1.5} onMouseEnter={enter} onTouchStart={enter} onFocus={enter} tabIndex={0} aria-label={text} />
            ) : (
              <rect key={`${l.name}-${m.prefix}`} x={m.x - m.r * 0.85} y={m.y - m.r * 0.85} width={m.r * 1.7} height={m.r * 1.7} rx={2}
                fill={l.color} fillOpacity={0.72} stroke="var(--surface-1)" strokeWidth={1.5} onMouseEnter={enter} onTouchStart={enter} onFocus={enter} tabIndex={0} aria-label={text} />
            )
          }),
        )}
      </svg>
      {hover && (
        <div className={s.tooltip} style={{ left: `${(hover.x / W) * 100}%`, top: `${(hover.y / H) * 100}%` }}>
          {hover.text}
        </div>
      )}
      {outside > 0 && <p className={s.note}>{fmt(outside)} outside the continental US, not shown.</p>}
    </div>
  )
}

function inContiguous([lat, lon]: readonly [number, number]): boolean {
  return lat > 24 && lat < 50 && lon > -125 && lon < -66
}

// Albers equal-area conic, the usual projection for US maps (standard parallels 29.5° and 45.5°).
const rad = Math.PI / 180
const n = (Math.sin(29.5 * rad) + Math.sin(45.5 * rad)) / 2
const C = Math.cos(29.5 * rad) ** 2 + 2 * n * Math.sin(29.5 * rad)
const rho0 = Math.sqrt(C - 2 * n * Math.sin(37.5 * rad)) / n
function albers(lat: number, lon: number): [number, number] {
  const rho = Math.sqrt(C - 2 * n * Math.sin(lat * rad)) / n
  const theta = n * (lon + 96) * rad
  return [rho * Math.sin(theta), -(rho0 - rho * Math.cos(theta))]
}

/** Scales the projection so the continental US fills a W×H box. */
function fitter(W: number, H: number) {
  const pts = [...ZIP3.values()].filter(inContiguous).map(([lat, lon]) => albers(lat, lon))
  const xs = pts.map((p) => p[0])
  const ys = pts.map((p) => p[1])
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)]
  const pad = 16
  const k = Math.min((W - pad * 2) / (maxX - minX), (H - pad * 2) / (maxY - minY))
  const ox = (W - (maxX - minX) * k) / 2
  const oy = (H - (maxY - minY) * k) / 2
  return (lat: number, lon: number): [number, number] => {
    const [x, y] = albers(lat, lon)
    return [ox + (x - minX) * k, oy + (y - minY) * k]
  }
}
